import { API_BASE_URL } from '../config/api';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

// Get all news articles with optional filters
export const getNews = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.status) queryParams.append('status', params.status);
    if (params.category) queryParams.append('category', params.category);
    if (params.subCategory) queryParams.append('subCategory', params.subCategory);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    if (params.search) queryParams.append('search', params.search);
    
    const url = `${API_BASE_URL}/news${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error('Failed to fetch news');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching news:', error);
    throw error;
  }
};

// Get published news only
export const getPublishedNews = async (params = {}) => {
  return getNews({ ...params, status: 'published' });
};

// Get single news article by ID
export const getNewsById = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/news/${id}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch news article');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching news article:', error);
    throw error;
  }
};

// Create new news article
export const createNews = async (newsData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/news`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(newsData),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || 'Failed to create news article');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating news:', error);
    throw error;
  }
};

// Update news article
export const updateNews = async (id, newsData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/news/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(newsData),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || 'Failed to update news article');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating news:', error);
    throw error;
  }
};

// Delete news article
export const deleteNews = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/news/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || 'Failed to delete news article');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error deleting news:', error);
    throw error;
  }
};

// Get related news articles (same category, excluding current)
export const getRelatedNews = async (category, excludeId, limit = 3) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/news?category=${encodeURIComponent(category)}&status=published&limit=${limit}`
    );
    
    if (!response.ok) {
      throw new Error('Failed to fetch related news');
    }
    
    const data = await response.json();
    // Filter out the current article
    const filteredNews = (data.news || data || []).filter(article => article.id !== excludeId);
    return filteredNews.slice(0, limit);
  } catch (error) {
    console.error('Error fetching related news:', error);
    return [];
  }
};

// Format date for display
export const formatNewsDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// Get unique categories from news
export const getNewsCategories = async () => {
  try {
    const { news } = await getNews({ limit: 100 });
    const categories = [...new Set(news.map(article => article.category).filter(Boolean))];
    return categories;
  } catch (error) {
    console.error('Error fetching news categories:', error);
    return [];
  }
};

// Update single news status
export const updateNewsStatus = async (id, status) => {
  try {
    const response = await fetch(`${API_BASE_URL}/news/${id}/status`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ status }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || 'Failed to update news status');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating news status:', error);
    throw error;
  }
};

// Bulk update news status (publish/archive)
export const bulkUpdateNewsStatus = async (ids, status) => {
  try {
    const response = await fetch(`${API_BASE_URL}/news/bulk-status`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ ids, status }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || 'Failed to bulk update news status');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error bulk updating news status:', error);
    throw error;
  }
};

// Bulk delete news articles
export const bulkDeleteNews = async (ids) => {
  try {
    const response = await fetch(`${API_BASE_URL}/news/bulk-delete`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ ids }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || 'Failed to bulk delete news');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error bulk deleting news:', error);
    throw error;
  }
};

export default {
  getNews,
  getPublishedNews,
  getNewsById,
  createNews,
  updateNews,
  deleteNews,
  getRelatedNews,
  formatNewsDate,
  getNewsCategories,
  updateNewsStatus,
  bulkUpdateNewsStatus,
  bulkDeleteNews
};
